import React, { useState, useEffect } from 'react';
import { Bot, Check, Clock, Crown, LogOut, Play, Plus, RefreshCw, Users, X } from 'lucide-react';
import { useLanguage } from './contexts/LanguageContext';
import { getTierMeta } from './utils/tierMeta';

const BOT_TYPE_LABELS = {
  smart: 'smart',
  aggressive: 'aggro',
  conservative: 'beginner',
  veteran: 'veteran',
  master: 'master',
  grandmaster: 'grandmaster',
  jev: 'jev'
};

function Room({ socket, roomInfo, currentUserId, onLeaveRoom, onGameStart }) {
  const { t } = useLanguage();
  const [room, setRoom] = useState(roomInfo);
  const [isHost, setIsHost] = useState(false);
  const socketId = socket?.id;

  const renderTierBadge = (tier, size = 'md') => {
    const meta = getTierMeta(tier);
    const label = t(meta.labelKey) || meta.defaultLabel;
    const sizeClass = size === 'small' ? 'tier-badge--small' : '';
    return (
      <span className={`tier-badge tier-badge-${meta.level} ${sizeClass}`.trim()} title={label}>
        {meta.icon}
      </span>
    );
  };

  const getBotTypeLabel = (type) => BOT_TYPE_LABELS[type] || 'Bot';

  useEffect(() => {
    if (!currentUserId) {
      console.log('Waiting for currentUserId...');
      return;
    }

    const handleRoomUpdate = (updatedRoom) => {
      console.log('Room update received:', updatedRoom);
      setRoom(updatedRoom);
    };

    const handleGameStarted = (data) => {
      onGameStart(data.gameState);
    };

    const handleUserDisconnected = (data) => {
      console.log(`User ${data.username} disconnected`);
    };

    const handleError = (errorData) => {
      console.error('🚨 Socket error:', errorData);
      alert(`Error: ${errorData.message}`);
    };

    socket.on('roomUpdate', handleRoomUpdate);
    socket.on('gameStarted', handleGameStarted);
    socket.on('userDisconnected', handleUserDisconnected);
    socket.on('error', handleError);

    return () => {
      socket.off('roomUpdate', handleRoomUpdate);
      socket.off('gameStarted', handleGameStarted);
      socket.off('userDisconnected', handleUserDisconnected);
      socket.off('error', handleError);
    };
  }, [socket, currentUserId, onGameStart]);

  useEffect(() => {
    if (!currentUserId && !socketId) {
      setIsHost(false);
      return;
    }
    const hostMatch = room?.hostId === currentUserId || (!!socketId && room?.hostId === socketId);
    setIsHost(Boolean(hostMatch));
  }, [room?.hostId, currentUserId, socketId]);

  const handleAddBot = (slotIndex) => {
    // No botType: the server owns the default. Sending 'smart' from here meant
    // changing the configured default had no effect on the Add Bot button,
    // because the client was overriding it on every call.
    socket.emit('addBot', {
      roomId: room.roomId,
      slotIndex
    });
  };

  const handleRemoveBot = (slotIndex) => {
    socket.emit('removeBot', { 
      roomId: room.roomId, 
      slotIndex 
    });
  };

  const handleToggleReady = () => {
    socket.emit('toggleReady', { 
      roomId: room.roomId 
    });
  };

  const handleStartGame = () => {
    socket.emit('startGame', { 
      roomId: room.roomId 
    });
  };

  const handleLeaveRoom = () => {
    socket.emit('leaveRoom');
    onLeaveRoom();
  };

  const handleSitHere = (slotIndex) => {
    console.log('🪑 Attempting to sit in slot:', slotIndex);
    console.log('🪑 Room ID:', room.roomId);

    // Send event to server to change seat
    socket.emit('changeSeat', {
      roomId: room.roomId,
      targetSlot: slotIndex
    });

    console.log('🪑 ChangeSeat event emitted');
  };

  const handleCycleBotType = (slotIndex) => {
    console.log('🤖 Cycling bot type for slot:', slotIndex);
    console.log('🤖 Room ID:', room.roomId);

    // Send event to server to cycle bot type
    socket.emit('cycleBotType', {
      roomId: room.roomId,
      slotIndex: slotIndex
    });

    console.log('🤖 CycleBotType event emitted');
  };

  // Create slots array (4 slots total)
  const slots = [];
  for (let i = 0; i < room.maxPlayers; i++) {
    const player = room.players.find(p => p && p.slot === i);
    slots.push({ index: i, player });
  }

  // Get current player
  const currentPlayer = room.players.find(p => {
    if (!p) return false;
    if (currentUserId && p.id === currentUserId) return true;
    if (socketId && p.socketId === socketId) return true;
    return false;
  });
  console.log('Current player found:', currentPlayer);
  console.log('Is host?', isHost);
  console.log('CurrentUserId:', currentUserId);

  // Show loading if waiting for socket connection
  if (!currentUserId) {
    return (
      <div className="room-page room-page--loading">
        <div className="room-loading">
          <span className="room-loading-dots" aria-hidden="true"><i /><i /><i /></span>
          <p>{t('room.connecting')}</p>
        </div>
      </div>
    );
  }

  // Convert room ID to display name (e.g., "lightning-falcon-ke9k" -> "Lightning Falcon")
  const getRoomDisplayName = (roomId) => {
    // Handle old format room-1234567890
    if (roomId.startsWith('room-')) {
      return roomId;
    }
    
    // Handle new format: "lightning-falcon-ke9k" -> "Lightning Falcon"
    // Split by dash and take only first two parts (ignore hash suffix)
    const parts = roomId.split('-');
    if (parts.length >= 2) {
      const adjective = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      const animal = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      return `${adjective} ${animal}`;
    }
    
    return roomId; // Fallback to original ID
  };

  // Seats 1 & 3 are team A and seats 2 & 4 team B (TEAM-01); partners sit opposite.
  const teams = [
    { key: 'a', label: 'A', seats: slots.filter(({ index }) => index % 2 === 0) },
    { key: 'b', label: 'B', seats: slots.filter(({ index }) => index % 2 === 1) }
  ];

  const renderSeat = ({ index, player }) => {
    if (!player) {
      return (
        <div key={index} className={`player-slot slot-${index} empty`}>
          <span className="slot-number">{index + 1}</span>
          <div className="empty-slot">
            <button type="button" className="sit-here-btn" onClick={() => handleSitHere(index)}>
              <b>{t('room.emptySlot')}</b>
              <small>{t('room.sitHere')}</small>
            </button>
          </div>
          {isHost && (
            <button type="button" onClick={() => handleAddBot(index)} className="add-bot-btn">
              <Plus aria-hidden="true" />
              <Bot aria-hidden="true" />
              <span>{t('room.addBot')}</span>
            </button>
          )}
        </div>
      );
    }

    const botType = player.botType || 'smart';
    const botLabel = getBotTypeLabel(botType);
    return (
      <div
        key={index}
        className={['player-slot', `slot-${index}`, 'occupied', player.isBot && 'is-bot', !player.isBot && player.isReady && 'is-ready'].filter(Boolean).join(' ')}
      >
        <span className="slot-number">{index + 1}</span>
        <span className={`ct-avatar slot-avatar ${player.isBot ? `slot-avatar--bot bot-${botType}` : ''}`.trim()} aria-hidden="true">
          {player.isBot ? <Bot /> : (player.username || '?').charAt(0).toUpperCase()}
        </span>
        <div className="player-info">
          <div className="player-name">
            {player.isBot && isHost ? (
              <button
                type="button"
                className={`bot-name-btn bot-${botType}`}
                onClick={() => handleCycleBotType(index)}
                data-tooltip={t('room.changeBotType', { type: botLabel })}
              >
                <span className="player-name-text">{player.username}</span>
                <span className="bot-type-pill">{botLabel}<RefreshCw aria-hidden="true" /></span>
              </button>
            ) : (
              <>
                <span className="player-name-text">
                  {player.username}
                  {!player.isBot && renderTierBadge(player.challengeTier || 0, 'small')}
                </span>
                {player.id === room.hostId && (
                  <span className="host-icon" title={t('room.host')} aria-label={t('room.host')}><Crown aria-hidden="true" /></span>
                )}
              </>
            )}
          </div>
          {player.isBot && !isHost && (
            <div className={`bot-type-pill read-only bot-${botType}`}>
              <Bot aria-hidden="true" />{botLabel}
            </div>
          )}
          {!player.isBot && (
            <div className="player-status">
              <span className={`ready-status ${player.isReady ? 'ready' : 'not-ready'}`}>
                {player.isReady ? <Check aria-hidden="true" /> : <Clock aria-hidden="true" />}
                {player.isReady ? t('room.ready') : t('room.notReady')}
              </span>
            </div>
          )}
        </div>
        {isHost && player.isBot && (
          <button
            type="button"
            onClick={() => handleRemoveBot(index)}
            className="remove-bot-btn"
            aria-label={t('room.removeBot')}
            title={t('room.removeBot')}
          >
            <X aria-hidden="true" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="room-page">
      <header className="lobby-topbar room-topbar">
        <div className="ct-brand">
          <span className="ct-brand-mark" aria-hidden="true">K</span>
          <span className="ct-brand-name">Kwajongen<small>Genk</small></span>
        </div>
        <span className="lobby-topbar-title">{t('room.waitingRoom')}</span>
        <span className="lobby-topbar-spacer" />
        <button type="button" onClick={handleLeaveRoom} className="ct-btn ct-btn--outline ct-btn--inline leave-btn">
          <LogOut aria-hidden="true" />
          <span>{t('room.leaveRoom')}</span>
        </button>
      </header>

      <main className="room-container">
        <section className="room-header">
          <div className="room-header-text">
            <span className="room-eyebrow">{t('room.room')}</span>
            <h1>{getRoomDisplayName(room.roomId)}</h1>
            <p>{t('room.subtitle')}</p>
          </div>
          <div className="room-status">
            <span className="ct-pill room-players">
              <Users aria-hidden="true" />
              {t('room.players')}: <b>{room.players.length}/{room.maxPlayers}</b>
            </span>
            {isHost && (
              <span className="ct-pill host-badge"><Crown aria-hidden="true" />{t('room.host')}</span>
            )}
          </div>
        </section>

        <div className="room-teams">
          {teams.map((team) => (
            <section key={team.key} className={`room-team room-team--${team.key}`}>
              <header className="room-team-head">
                <h2>{t('room.team', { team: team.label })}</h2>
                <span>{t('room.teamSeats', { first: team.seats[0].index + 1, second: team.seats[1].index + 1 })}</span>
              </header>
              <div className="room-team-seats">
                {team.seats.map(renderSeat)}
              </div>
            </section>
          ))}
        </div>

        <div className="room-actions">
          {currentPlayer && !currentPlayer.isBot && (
            <button
              type="button"
              onClick={handleToggleReady}
              aria-pressed={Boolean(currentPlayer.isReady)}
              className={`ct-btn ct-btn--inline ready-btn ${currentPlayer.isReady ? 'ready' : 'not-ready'}`}
            >
              {currentPlayer.isReady ? <Check aria-hidden="true" /> : <Clock aria-hidden="true" />}
              {currentPlayer.isReady ? t('room.ready') : t('room.notReady')}
            </button>
          )}

          {isHost ? (
            <button
              type="button"
              onClick={handleStartGame}
              disabled={!room.canStart}
              className="ct-btn ct-btn--gold ct-btn--inline start-game-btn"
            >
              <Play aria-hidden="true" />
              {room.canStart ? t('room.startGame') : `${t('room.waitingForPlayers')} (${room.players.length}/4)`}
            </button>
          ) : (
            <p className="room-actions-hint">{t('room.waitingForHost')}</p>
          )}
        </div>
      </main>
    </div>
  );
}

export default Room;
